import { useRef } from 'react';
import { Button } from './Button';
import type { ButtonProps } from './Button';

// FileButton (story 6-8) — the design-system's single-file picker primitive:
// a Button that opens the native file picker, reporting the chosen File via
// `onSelect`. G-22 (DOD §4): "new UI composes a DS primitive or adds one
// here first" — `PhotoInput.tsx` is the DS's existing file-upload primitive,
// but it is shaped for MULTIPLE evidence photos with per-item progress/
// retry/caption state (`PhotoInputItem[]`); a single named resource (an org
// logo, a user avatar, …) that uploads immediately on pick and shows its own
// preview elsewhere has no shared shape with that one. This is the smaller
// primitive underneath both: `PhotoInput` could compose this per-item in a
// future refactor, but that is not this story's scope (DOD §1.1: touch what
// the story owns).
export interface FileButtonProps extends Omit<ButtonProps, 'onSelect' | 'type' | 'onChange'> {
  /** Native `accept` attribute (MIME types / extensions), e.g. "image/png,image/jpeg". */
  accept?: string;
  /** Called with the chosen file. Never called on cancel (no file picked). */
  onSelect: (file: File) => void;
  /** Accessible name for the hidden `<input type="file">` itself. */
  inputLabel: string;
}

export function FileButton({ accept, onSelect, inputLabel, children, disabled, ...buttonProps }: FileButtonProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      <Button type="button" disabled={disabled} onClick={() => inputRef.current?.click()} {...buttonProps}>
        {children}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        disabled={disabled}
        className="sr-only"
        aria-label={inputLabel}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = ''; // allow re-picking the same file next time
          if (file) onSelect(file);
        }}
      />
    </>
  );
}
